import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import i18next from "src/i18n/config";
import { RootState } from "src/store";
import { useCurrentWorkspaceEvents } from "src/store/api/events/helpers";
import { EventLevelHeader, getEventLevels } from "src/ui/shared/table/headers/EventLevelHeader";
import { makeStyles } from "tss-react/mui";

import { showDetails } from "@dashboard/devices/store/index";
import { selectDevicesByGroupId } from "@dashboard/devices/store/selectors/devices";
import { EventReport, Group } from "@dashboard/devices/types";
import { selectGroups } from "@dashboard/groups/store/selectors";
import { selectLicensesEntities } from "@dashboard/licenses/store/selectors";
import { EventContentResult, renderEventContent } from "@dashboard/shared/components/events";
import { Box, Collapse, Theme, Typography, useTheme } from "@mui/material";
import { useFetchBuildsForProductsQuery } from "@products/api";
import { useCurrentWorkspaceProducts } from "@products/api/helpers";
import TimeService from "@services/timeService/timeService";
import { ExpandToggleButton } from "@shared/ExpandToggleButton";
import { MinimalTableDetailsRow } from "@shared/table/CollapsibleRow";
import { MinimalTable, MinimalTableCell, MinimalTableHeader, MinimalTableRow } from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { Header, TableHeaderRows } from "@shared/table/TableHeader";
import CustomTablePagination from "@shared/table/TablePagination";
import { TableRowCells } from "@shared/table/TableRowCells";
import { useQueryErrorToast } from "@shared/toasts/useQueryErrorToast";
import { Tooltip } from "@shared/Tooltip";
import {
    ColumnDef,
    ColumnFiltersState,
    ExpandedState,
    getCoreRowModel,
    getExpandedRowModel,
    OnChangeFn,
    PaginationState,
    SortingState,
    useReactTable,
} from "@tanstack/react-table";

import { useDeviceNameClickHandler } from "../../shared/EventTab/EventTab.utils";
import { PaneElement } from "../../shared/PaneElement";

const useStyles = makeStyles()((t: Theme) => ({
    expandedContent: {
        display: "flex",
        flexDirection: "column",
        gap: t.spacing(2),
        padding: t.spacing(2),
        paddingLeft: t.spacing(4),
    },
}));

const createColumns = (
    getDeviceName: (event: EventReport) => string,
    getEventDetails: (event: EventReport) => EventContentResult,
): ColumnDef<EventReport, unknown>[] => [
    {
        id: "expander",
        header: () => null,
        cell: ({ row }) => (
            <MinimalTableCell>
                <ExpandToggleButton
                    expanded={row.getIsExpanded()}
                    onClick={(e) => {
                        e.stopPropagation();
                        row.toggleExpanded();
                    }}
                />
            </MinimalTableCell>
        ),
        enableSorting: false,
    },
    {
        header: i18next.t("sidepanels:groupPanel.table.header.date"),
        accessorKey: "created",
        enableSorting: true,
        sortDescFirst: true,
        cell: (info) => {
            const dateTime = TimeService.getLocalDate(info.row.original.created, "ISO", "dateTime");
            const date = TimeService.getLocalDate(info.row.original.created, "ISO", "date");
            return (
                <MinimalTableCell>
                    <Tooltip title={dateTime}>
                        <Typography variant="body1">{date}</Typography>
                    </Tooltip>
                </MinimalTableCell>
            );
        },
    },
    {
        id: "eventLevel",
        header: () => i18next.t("common:label.type"),
        accessorFn: (row) => {
            return row.level;
        },
        filterFn: (row, _columnId, filterValue: string[]) => {
            return filterValue.includes(String(row.original.level));
        },
        cell: ({ row }) => {
            const level =
                row.original.level === "warning"
                    ? i18next.t("common:table.filter.warning")
                    : i18next.t("common:table.filter.notice");
            return (
                <MinimalTableCell>
                    <Typography variant="body1">{level}</Typography>
                </MinimalTableCell>
            );
        },
        enableSorting: false,
    },
    {
        header: i18next.t("common:label.event"),
        accessorKey: "eventTypeName",
        enableSorting: false,
        cell: (info) => {
            const eventDetails = getEventDetails(info.row.original);
            return (
                <MinimalTableCell>
                    <Typography variant="body1">{eventDetails.header}</Typography>
                </MinimalTableCell>
            );
        },
    },
    {
        header: i18next.t("common:label.device"),
        accessorKey: "entityId",
        enableSorting: false,
        cell: (info) => {
            const isGroupEvent = info.row.original.eventTypeName.startsWith("GroupEvent.");
            const deviceName = getDeviceName(info.row.original);
            return (
                <MinimalTableCell>
                    {isGroupEvent ? (
                        <Tooltip title={i18next.t("sidepanels:groupPanel.table.item.notApplicableForDevices")}>
                            <Typography variant="body1">-</Typography>
                        </Tooltip>
                    ) : (
                        <Typography variant="body1">{deviceName}</Typography>
                    )}
                </MinimalTableCell>
            );
        },
    },
];

const EventTab = (props: Group) => {
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
    const [expanded, setExpanded] = React.useState<ExpandedState>({});
    const [pagination, setPagination] = React.useState<PaginationState>({ pageIndex: 0, pageSize: 10 });
    const [sorting, setSorting] = React.useState<SortingState>([{ id: "created", desc: true }]);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation(["sidepanels", "common"]);
    const { classes } = useStyles();
    const theme = useTheme();
    const allGroups = useSelector(selectGroups);

    const { data: products = [] } = useCurrentWorkspaceProducts();
    const { data: builds = [] } = useFetchBuildsForProductsQuery(products.map((product) => product.id));

    const levels = React.useMemo(() => getEventLevels(columnFilters), [columnFilters]);
    const events = useCurrentWorkspaceEvents({
        groupIds: [props.id],
        allWorkspaceGroupsLength: allGroups.length,
        levels,
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
        sortCriteria: sorting[0]?.id,
        sortDirection: sorting[0] ? (sorting[0].desc ? "descending" : "ascending") : undefined,
    });
    const { data: eventReports, isLoading } = events;
    useQueryErrorToast(events);
    const data = React.useMemo(() => eventReports?.list || [], [eventReports?.list]);

    const devices = useSelector((state: RootState) => selectDevicesByGroupId(state, props.id));
    const licensesEntities = useSelector((state: RootState) => selectLicensesEntities(state));

    const getDeviceName = (event: EventReport) => {
        const isLicenseEvent = event.eventTypeName.startsWith("LicenseEvent.");
        const license = isLicenseEvent ? licensesEntities[parseInt(event.entityId)] : undefined;
        const device = devices.find((d) => d?.id.toString() === event.entityId) || (license?.device ? license.device : undefined);
        return device?.name ?? t("sidepanels:groupPanel.table.item.unknownDevice");
    };

    const handleDeviceNameClick = useDeviceNameClickHandler({
        devices: devices,
        licensesEntities,
        groupId: props.id,
    });

    const getEventDetails = (event: EventReport) => {
        const isLicenseEvent = event.eventTypeName.startsWith("LicenseEvent.");
        const license = isLicenseEvent ? licensesEntities[parseInt(event.entityId)] : undefined;
        const device = devices.find((d) => d?.id.toString() === event.entityId) || (license?.device ? license.device : undefined);

        const handleLicenseClickForEvent = license
            ? () => {
                  dispatch(
                      showDetails({
                          type: "license",
                          selectedId: license.id,
                      }),
                  );
              }
            : undefined;

        return renderEventContent(
            event,
            {
                license,
                device,
                navigate,
                group: props,
                allGroups,
                products,
                builds,
                handleDeviceNameClick,
                handleLicenseClick: handleLicenseClickForEvent,
            },
            theme,
        );
    };

    const columns = createColumns(getDeviceName, getEventDetails);

    const handleColumnFiltersChange: OnChangeFn<ColumnFiltersState> = (updater) => {
        setColumnFilters(updater);
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    };

    const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
        setSorting(updater);
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    };

    const table = useReactTable<EventReport>({
        data,
        columns,
        state: { columnFilters, expanded, pagination, sorting },
        getRowId: (row) => row.id,
        onExpandedChange: setExpanded,
        getRowCanExpand: () => true,
        getSubRows: () => undefined,
        onSortingChange: handleSortingChange,
        manualPagination: true,
        manualFiltering: true,
        manualSorting: true,
        rowCount: eventReports?.count ?? 0,
        onPaginationChange: setPagination,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        onColumnFiltersChange: handleColumnFiltersChange,
    });

    const handleResetFilter = () => {
        setColumnFilters([]);
        setExpanded({});
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    };

    const { pageSize, pageIndex } = pagination;

    return (
        <PaneElement.Container>
            <PaneElement.Header>{t("common:label.events")}</PaneElement.Header>
            <PaneElement.TableLayout
                contentDeps={[pageIndex, pageSize, data.length]}
                pagination={
                    <CustomTablePagination
                        component="div"
                        rowsPerPageOptions={[10, 24, 48]}
                        count={eventReports?.count ?? 0}
                        rowsPerPage={pageSize}
                        page={pageIndex}
                        onPageChange={(_event, page) => setPagination((prev) => ({ ...prev, pageIndex: page }))}
                        onRowsPerPageChange={(e) => {
                            const size = e.target.value ? Number(e.target.value) : 10;
                            setPagination({ pageIndex: 0, pageSize: size });
                        }}
                    />
                }
            >
                <MinimalTable>
                    <MinimalTableHeader>
                        <TableHeaderRows
                            headerGroups={table.getHeaderGroups()}
                            renderHeader={(header) => (
                                <Header {...header}>
                                    {header.id === "eventLevel" && (
                                        <EventLevelHeader
                                            columnFilters={columnFilters}
                                            setColumnFilters={handleColumnFiltersChange}
                                        />
                                    )}
                                </Header>
                            )}
                        />
                    </MinimalTableHeader>
                    <TableBody
                        table={table}
                        filter={{ isActive: !!columnFilters.length, onResetFilter: handleResetFilter }}
                        loading={isLoading}
                        noDataMessage={t("sidepanels:groupPanel.table.noEvents")}
                    >
                        {table.getRowModel().rows.map((row) => {
                            const eventDetails = getEventDetails(row.original);
                            return (
                                <React.Fragment key={row.original.id}>
                                    <MinimalTableRow>
                                        <TableRowCells row={row} />
                                    </MinimalTableRow>
                                    <MinimalTableDetailsRow colSpan={columns.length}>
                                        <Collapse in={row.getIsExpanded()} timeout="auto" unmountOnExit>
                                            <Box className={classes.expandedContent}>
                                                <Typography variant="body1" component="div">
                                                    {eventDetails.component}
                                                </Typography>
                                                {eventDetails.details}
                                            </Box>
                                        </Collapse>
                                    </MinimalTableDetailsRow>
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </MinimalTable>
            </PaneElement.TableLayout>
        </PaneElement.Container>
    );
};

export default EventTab;
